import { Button, Group, SegmentedControl, Switch, Text, Textarea } from '@mantine/core';
import {
  MAX_PLAYBOOK_LENGTH,
  PLAYBOOK_ERROR_LENGTH,
  PLAYBOOK_WARN_LENGTH,
  playbookLength,
} from '../../../helpers/agentFieldMapping';
import { blankAgentUi, updateAgentUi } from '../../../helpers/agentUi';
import HelpPopover from '../../HelpPopover/HelpPopover';
import { formatDate } from '../../KnowledgeBase/shared';
import SettingRow from '../../SettingRow/SettingRow';
import { hasAgentUi } from '../chatBotTabs';
import { HELP } from './superAdminHelp';

// The agentUi switches, shown once the API returns agentUi; a change on a null agentUi starts from the defaults.
const AgentUiRows = ({ agentUi, handleChange }) => {
  const set = (changes) => handleChange({ target: { name: 'agentUi', value: updateAgentUi(agentUi, changes) } });
  const { context, location } = { ...blankAgentUi(), ...agentUi };

  return (
    <>
      <SettingRow label="Today" htmlFor="agentUiToday" help={<HelpPopover {...HELP.contextToday} />}>
        <Switch
          id="agentUiToday"
          size="xs"
          checked={context.today === true}
          onChange={(e) => set({ context: { ...context, today: e.currentTarget.checked } })}
        />
      </SettingRow>
      <SettingRow label="Page context" htmlFor="agentUiPage" help={<HelpPopover {...HELP.contextPage} />}>
        <Switch
          id="agentUiPage"
          size="xs"
          checked={context.page === true}
          onChange={(e) => set({ context: { ...context, page: e.currentTarget.checked } })}
        />
      </SettingRow>
      <SettingRow label="Shopper location" help={<HelpPopover {...HELP.location} />}>
        <Group gap="md">
          <SegmentedControl
            size="xs"
            aria-label="Shopper location"
            data={[
              { value: 'off', label: 'Off' },
              { value: 'ask', label: 'Ask' },
            ]}
            value={location.mode}
            onChange={(mode) => set({ location: { ...location, mode } })}
          />
          <Group gap={4} wrap="nowrap">
            <Switch
              size="xs"
              label="IP city"
              checked={location.ipCity === true}
              onChange={(e) => set({ location: { ...location, ipCity: e.currentTarget.checked } })}
            />
            <HelpPopover {...HELP.ipCity} />
          </Group>
        </Group>
      </SettingRow>
    </>
  );
};

const BehaviourContextSection = ({ chatBot, handleChange, onSectionChange }) => {
  const playbookCount = playbookLength(chatBot.agentPlaybook);
  const playbookOver = playbookCount > MAX_PLAYBOOK_LENGTH;
  const playbookLong = playbookCount > PLAYBOOK_WARN_LENGTH;
  const meterColor = playbookCount > PLAYBOOK_ERROR_LENGTH ? 'danger' : playbookLong ? 'warning.9' : 'dimmed';
  const setPlaybook = (value) => handleChange({ target: { name: 'agentPlaybook', value: value || null } });

  // An example is added as one more line, not in place of what is there.
  const usePlaybookExample = (line) => {
    const previous = chatBot.agentPlaybook ?? null;
    setPlaybook(previous ? `${previous}\n${line}` : line);
    return () => setPlaybook(previous);
  };

  return (
    <>
      <SettingRow
        label="Playbook"
        htmlFor="agentPlaybook"
        top
        help={<HelpPopover {...HELP.agentPlaybook} onUse={usePlaybookExample} />}
      >
        <Textarea
          id="agentPlaybook"
          size="xs"
          maw={760}
          autosize
          minRows={3}
          maxRows={12}
          value={chatBot.agentPlaybook ?? ''}
          error={playbookOver ? `At most ${MAX_PLAYBOOK_LENGTH} characters` : null}
          onChange={(e) => setPlaybook(e.currentTarget.value)}
        />
        <Group gap="xs" align="flex-start" wrap="nowrap" maw={760} mt={2}>
          {playbookLong && (
            <Text size="xs" c={meterColor}>
              Long playbooks are followed less reliably. Move rules about one tool&apos;s results into that tool&apos;s
              guidance.
            </Text>
          )}
          <Text size="xs" c={meterColor} ml="auto" style={{ whiteSpace: 'nowrap' }} data-testid="playbook-meter">
            {playbookCount} / {MAX_PLAYBOOK_LENGTH}
          </Text>
          {onSectionChange && (
            <Button variant="subtle" size="compact-xs" onClick={() => onSectionChange('history')}>
              History
            </Button>
          )}
        </Group>
        {chatBot.learningLastApplied && (
          <Text size="xs" c="dimmed" maw={760}>
            Last change from Learning · {chatBot.learningLastApplied.title} ·{' '}
            {formatDate(chatBot.learningLastApplied.at)}
          </Text>
        )}
      </SettingRow>

      <SettingRow
        label="Order status lookup"
        htmlFor="orderStatusEnabled"
        help={<HelpPopover {...HELP.orderStatusEnabled} />}
      >
        <Switch
          id="orderStatusEnabled"
          size="xs"
          checked={chatBot.orderStatusEnabled !== false}
          onChange={(e) => handleChange({ target: { name: 'orderStatusEnabled', value: e.currentTarget.checked } })}
        />
      </SettingRow>

      {hasAgentUi(chatBot) && <AgentUiRows agentUi={chatBot.agentUi} handleChange={handleChange} />}
    </>
  );
};

export default BehaviourContextSection;
